import React, { useState } from "react";
import { toast } from "sonner";
import { Settings as SettingsIcon, Palette, LayoutGrid, Eye, EyeOff, ChevronUp, ChevronDown, Check, Lock } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import BillingCard from "@/components/BillingCard";

const THEMES = [
  { id: "periwinkle", name: "Periwinkle", color: "#8FA9E3", pro: false },
  { id: "mint", name: "Fresh Mint", color: "#6FC2B0", pro: true },
  { id: "peach", name: "Soft Peach", color: "#EE9C86", pro: true },
  { id: "lavender", name: "Lavender", color: "#B69CE0", pro: true },
  { id: "sky", name: "Sky Blue", color: "#78B7E0", pro: true },
];

const WIDGET_LABELS = {
  agenda: "Today's Agenda",
  reminders: "Reminders",
  quicklinks: "Quick Links",
  lessons: "Recent Lesson Plans",
  notes: "Sticky Notes",
  students: "Students",
};

const DEFAULT_WIDGETS = [
  { id: "agenda", visible: true }, { id: "reminders", visible: true },
  { id: "quicklinks", visible: true }, { id: "lessons", visible: true },
  { id: "notes", visible: true }, { id: "students", visible: true },
];

export default function Settings() {
  const { user, setUser, applyTheme, refreshUser } = useAuth();
  const navigate = useNavigate();
  const prefs = user?.preferences || {};
  const [theme, setTheme] = useState(prefs.theme || "periwinkle");
  const [widgets, setWidgets] = useState(prefs.widgets?.length ? prefs.widgets : DEFAULT_WIDGETS);
  const [saving, setSaving] = useState(false);

  const pickTheme = (t) => {
    if (t.pro && !user?.is_pro) {
      toast.error("Premium themes are a Pro feature");
      navigate("/pricing");
      return;
    }
    setTheme(t.id); applyTheme(t.id);
  };

  const toggleWidget = (id) => setWidgets((w) => w.map((x) => (x.id === id ? { ...x, visible: !x.visible } : x)));

  const move = (idx, dir) => {
    const next = [...widgets];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setWidgets(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/preferences", { theme, widgets });
      setUser({ ...user, preferences: data });
      applyTheme(data.theme);
      await refreshUser();
      toast.success("Preferences saved");
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Settings" icon={SettingsIcon} subtitle="Personalize your command center — themes and dashboard layout." />

      <div className="mb-6">
        <BillingCard />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="tcc-card p-6">
          <div className="flex items-center gap-2 mb-5"><Palette size={19} className="text-primary" /><h2 className="font-heading font-bold text-slate-800">Color theme</h2></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {THEMES.map((t) => (
              <button key={t.id} onClick={() => pickTheme(t)} data-testid={`settings-theme-${t.id}`}
                className={`flex items-center gap-2.5 p-3 rounded-2xl border-2 transition-all ${theme === t.id ? "border-slate-800 bg-white" : "border-slate-100 bg-white hover:border-slate-300"}`}>
                <span className="h-8 w-8 rounded-full" style={{ background: t.color }} />
                <span className="text-sm font-medium text-slate-700">{t.name}</span>
                {t.pro && !user?.is_pro ? <Lock size={13} className="text-slate-400 ml-auto" /> : theme === t.id && <Check size={15} className="text-primary ml-auto" />}
              </button>
            ))}
          </div>
        </div>

        <div className="tcc-card p-6">
          <div className="flex items-center gap-2 mb-5"><LayoutGrid size={19} className="text-primary" /><h2 className="font-heading font-bold text-slate-800">Dashboard widgets</h2></div>
          <div className="space-y-2">
            {widgets.map((w, i) => (
              <div key={w.id} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50" data-testid={`widget-row-${w.id}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-primary disabled:opacity-30"><ChevronUp size={16} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === widgets.length - 1} className="text-slate-400 hover:text-primary disabled:opacity-30"><ChevronDown size={16} /></button>
                </div>
                {w.visible ? <Eye size={17} className="text-primary" /> : <EyeOff size={17} className="text-slate-300" />}
                <span className="flex-1 text-sm font-medium text-slate-700">{WIDGET_LABELS[w.id] || w.id}</span>
                <Switch checked={w.visible} onCheckedChange={() => toggleWidget(w.id)} data-testid={`widget-toggle-${w.id}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="settings-save-button" className="rounded-full bg-primary hover:bg-primary/90 h-11 px-8 mt-6">
        {saving ? "Saving…" : "Save preferences"}
      </Button>
    </div>
  );
}
